import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { roundSnapshot, validStake } from "./minesModel";

/** One synchronous mutation lock covers start, reveal AND cashout. No optimistic
 * outcome or balance. Failed mutations reconcile the exact server round before
 * allowing another action; failed initial hydration cannot silently start fresh. */
export default function useMinesGame(refreshWallet) {
  const [round, setRound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncError, setSyncError] = useState("");
  const [error, setError] = useState("");
  const [pendingTile, setPendingTile] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState(false);
  const [edgePct, setEdgePct] = useState(null);
  const current = useRef(null);
  const locked = useRef(false);
  const syncGeneration = useRef(0);
  const mounted = useRef(false);
  const refreshRef = useRef(refreshWallet);
  refreshRef.current = refreshWallet;

  const publish = useCallback((value) => {
    current.current = value;
    if (mounted.current) setRound(value);
    return value;
  }, []);
  const updateWallet = useCallback(async () => {
    // A wallet refresh failure must not turn a completed game action into a retry.
    try { await refreshRef.current?.(); } catch { /* auth layer owns refresh errors */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/casino/history");
      if (mounted.current) { setHistory((Array.isArray(data) ? data : []).filter((h) => h.game === "mines")); setHistoryError(false); }
    } catch { if (mounted.current) setHistoryError(true); }
  }, []);
  const reconcile = useCallback(async (id) => {
    const generation = ++syncGeneration.current;
    const { data } = await api.get(id ? `/casino/mines/round/${encodeURIComponent(id)}` : "/casino/mines/active");
    if (!mounted.current || generation !== syncGeneration.current) return null;
    const next = data?.round_id ? roundSnapshot(data) : null;
    publish(next);
    if (mounted.current) setSyncError("");
    return next;
  }, [publish]);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    reconcile().catch(() => {
      if (!cancelled) setSyncError("Couldn’t check your active round. Reconnect before playing.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    loadHistory();
    api.get("/casino/house-edges").then(({ data }) => {
      const value = Number(data?.edges?.mines);
      if (!cancelled && data?.edges?.mines != null && Number.isFinite(value)) setEdgePct(value);
    }).catch(() => { /* previews stay unavailable; server play remains authoritative */ });
    return () => { cancelled = true; mounted.current = false; syncGeneration.current += 1; };
  }, [reconcile, loadHistory]);

  const retrySync = useCallback(async () => {
    if (locked.current) return;
    locked.current = true; setLoading(true); setError("");
    try { await reconcile(current.current?.round_id); await updateWallet(); await loadHistory(); }
    catch { if (mounted.current) setSyncError("Still unable to sync your round. Please try again."); }
    finally { locked.current = false; if (mounted.current) setLoading(false); }
  }, [reconcile, updateWallet, loadHistory]);

  const mutate = useCallback(async (operation, payload) => {
    if (locked.current || loading || syncError) return;
    const before = current.current;
    if (operation !== "start" && before?.status !== "active") return;
    if (operation === "start" && before?.status === "active") return;
    if (operation === "reveal" && before.revealed.includes(payload.tile)) return;
    if (operation === "cashout" && !before.revealed.length) return;
    if (operation === "start" && !validStake(payload.bet)) { setError("Enter a positive stake with up to two decimal places."); return; }
    locked.current = true; setBusy(true); setError("");
    if (operation === "reveal") setPendingTile(payload.tile);
    let submittedId = operation === "start" ? null : before.round_id;
    try {
      const request = operation === "start" ? payload : { round_id: before.round_id, ...payload };
      const { data } = await api.post(`/casino/mines/${operation}`, request);
      if (operation === "start") {
        submittedId = data.round_id;
        publish(roundSnapshot(data, { ...payload, status: "active", revealed: [], multiplier: 1, cashout_value: 0 }));
        await updateWallet();
      } else if (operation === "cashout") {
        publish(roundSnapshot({ ...data, status: "won" }, before));
        await updateWallet(); await loadHistory();
      } else if (data.mine) {
        publish(roundSnapshot({ ...data, status: "lost", hit: payload.tile }, before));
        await updateWallet(); await loadHistory();
      } else {
        const next = roundSnapshot({ ...data, revealed: [...before.revealed, payload.tile], status: "active" }, before);
        publish(next);
        if (next.revealed.length === 25 - next.mines) {
          // Final safe tile: use the SAME idempotent server cashout endpoint.
          const paid = await api.post("/casino/mines/cashout", { round_id: next.round_id });
          publish(roundSnapshot({ ...paid.data, status: "won" }, next));
          await updateWallet(); await loadHistory();
        }
      }
    } catch (e) {
      const message = apiError(e?.response?.data?.detail);
      if (mounted.current) setError(e?.response ? message : "Connection interrupted. Checking your round…");
      try {
        await reconcile(submittedId);
        await updateWallet(); await loadHistory();
        if (mounted.current && !e?.response) setError("Round synchronized. Review the board before continuing.");
      } catch {
        if (mounted.current) setSyncError("Your last action could not be confirmed. Reconnect to check the server before continuing.");
      }
    } finally {
      locked.current = false;
      if (mounted.current) { setBusy(false); setPendingTile(null); }
    }
  }, [loading, syncError, publish, reconcile, updateWallet, loadHistory]);

  return { round, busy, loading, syncError, error, pendingTile, history, historyError, edgePct, retrySync, loadHistory,
    start: (bet, mines, currency) => mutate("start", { bet, mines, currency }),
    reveal: (tile) => mutate("reveal", { tile }), cashout: () => mutate("cashout", {}),
  };
}
